import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Search, User, Menu, X, Truck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import CartDrawer from "@/components/CartDrawer";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const NAV = [
  { label: "Shop All", to: "/shop" },
  { label: "Kurtis & Tunics", to: "/shop?category=Kurtis%20%26%20Tunics" },
  { label: "Suit Sets", to: "/shop?category=Suit%20Sets" },
  { label: "Anarkalis", to: "/shop?category=Anarkalis" },
  { label: "Fusion Dresses", to: "/shop?category=Fusion%20Dresses" },
  { label: "Track Order", to: "/track" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const navigate = useNavigate();

  React.useEffect(() => {
    api.get("/settings").then((r) => setAnnouncement(r.data.announcement || "")).catch(() => {});
  }, []);

  const annText = announcement || "FLAT 15% OFF ON FIRST ORDER  •  FREE SHIPPING ABOVE ₹800  •  3-DAY EASY RETURNS  •  INSTANT WHATSAPP ORDER UPDATES";

  return (
    <>
      <div className="bg-rose-950 text-amber-50 text-center py-2 overflow-hidden">
        <div className="marquee accent-label !text-amber-50/90" data-testid="top-banner">
          ✦ {annText} &nbsp;•&nbsp; {annText} &nbsp;•&nbsp;
        </div>
      </div>
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#fdfbf7]/90 border-b border-rose-900/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="w-6 h-6 text-rose-950" />
            </button>
            <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
              <img src="/logo.png" alt="Million Pixels" className="h-10 lg:h-12 w-auto object-contain" />
              <span className="font-serif text-xl lg:text-2xl font-bold tracking-tight text-rose-950 leading-none">MILLION<span className="text-amber-600"> PIXELS</span></span>
            </Link>
            <nav className="hidden lg:flex items-center gap-6">
              {NAV.map((n) => (
                <Link key={n.label} to={n.to} className="font-accent text-sm font-medium text-rose-950/80 hover:text-amber-600 transition-colors" data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  {n.label}
                </Link>
              ))}
            </nav>
            <div className="flex items-center gap-3 lg:gap-4">
              <Link to="/track" className="hidden sm:block" data-testid="header-track-link">
                <Truck className="w-5 h-5 text-rose-950 hover:text-amber-600 transition-colors" />
              </Link>
              <button onClick={() => navigate(user ? "/account" : "/login")} data-testid="header-account-btn">
                <User className="w-5 h-5 text-rose-950 hover:text-amber-600 transition-colors" />
              </button>
              <button onClick={() => setOpen(true)} className="relative" data-testid="header-cart-btn">
                <ShoppingBag className="w-5 h-5 text-rose-950 hover:text-amber-600 transition-colors" />
                {count > 0 && (
                  <span className="absolute -top-2 -right-2 bg-amber-600 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center" data-testid="cart-count">{count}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-[#fdfbf7] p-6 fade-up">
            <div className="flex justify-between items-center mb-8">
              <span className="font-serif text-xl font-bold text-rose-950">Menu</span>
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close"><X className="w-6 h-6" /></button>
            </div>
            <div className="flex flex-col gap-4">
              {NAV.map((n) => (
                <Link key={n.label} to={n.to} onClick={() => setMobileOpen(false)} className="font-accent text-base text-rose-950/80">{n.label}</Link>
              ))}
              <Link to={user ? "/account" : "/login"} onClick={() => setMobileOpen(false)} className="font-accent text-base text-amber-600 font-semibold">{user ? "My Account" : "Login / Signup"}</Link>
            </div>
          </div>
        </div>
      )}
      <CartDrawer />
    </>
  );
}
